import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';

/** US-Letter fallback placeholder height (72 dpi) used before the real PDF page metadata loads. */
export const PDF_PLACEHOLDER_HEIGHT = 792;

/** Shared zoom bounds — keep in sync with the toolbar zoom buttons in both viewers. */
export const PDF_MIN_SCALE = 0.5;
export const PDF_MAX_SCALE = 2.0;

interface PdfViewportPage {
  getViewport: (params: { scale: number }) => { width: number; height: number };
}

interface OnDocumentLoadSuccessArgs {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PdfViewportPage>;
}

interface PdfPageDimensions {
  width: number;
  height: number;
}

export interface UsePdfPageRendererOptions {
  /** Used as an effect dependency so observers/scale re-attach when the document changes. */
  pdfUrl: string;
  /** Controlled current page (1-indexed). If omitted, the hook manages it internally. */
  currentPage?: number;
  onCurrentPageChange?: (page: number) => void;
}

/**
 * Shared react-pdf rendering/zoom/lazy-load logic for the template editor
 * (PdfTemplateViewer) and the homeowner-facing form viewer (PublicFormViewer).
 * Single source of truth so a scaling/zoom fix can't drift between the two.
 */
export function usePdfPageRenderer({ pdfUrl, currentPage: controlledCurrentPage, onCurrentPageChange }: UsePdfPageRendererOptions) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [numPages, setNumPages] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [pageAspectRatio, setPageAspectRatio] = useState<number>(792 / 612);
  const [pageDimensions, setPageDimensions] = useState<Map<number, PdfPageDimensions>>(new Map());
  const [scale, setScaleState] = useState(1.0);
  // Counter that increments on every document load to force observer re-attach
  const [docLoadCount, setDocLoadCount] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Stable ref objects per page — identity never changes, only .current is mutated
  const pageRefMap = useRef<Map<number, React.RefObject<HTMLDivElement | null>>>(new Map());
  // Pages near the viewport — only these render the heavy <Page> component
  const [nearViewport, setNearViewport] = useState<Set<number>>(new Set());
  // Measured height of each page (recorded on first render) for placeholder sizing
  const measuredHeights = useRef<Map<number, number>>(new Map());

  const fitWidth = containerWidth || 260;
  const mobileRenderWidth = Math.max(1, Math.floor(fitWidth * scale));
  const renderSize = isDesktop ? `desktop:${scale}` : `mobile:${mobileRenderWidth}`;
  const previousRenderSize = useRef(renderSize);
  if (previousRenderSize.current !== renderSize) {
    measuredHeights.current.clear();
    previousRenderSize.current = renderSize;
  }

  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const currentPage = controlledCurrentPage ?? internalCurrentPage;
  const setCurrentPage = useCallback(
    (page: number) => {
      if (onCurrentPageChange) onCurrentPageChange(page);
      else setInternalCurrentPage(page);
    },
    [onCurrentPageChange],
  );

  /** Get or create a stable RefObject for the given page index */
  const getPageRef = useCallback((pageIndex: number): React.RefObject<HTMLDivElement | null> => {
    let ref = pageRefMap.current.get(pageIndex);
    if (!ref) {
      ref = { current: null };
      pageRefMap.current.set(pageIndex, ref);
    }
    return ref;
  }, []);

  const onDocumentLoadSuccess = useCallback(async ({ numPages: np, getPage }: OnDocumentLoadSuccessArgs) => {
    setNumPages(np);

    // Load every page's source dimensions. Mobile renders all pages at the same
    // pixel width, so each overlay needs that page's own points-to-pixels ratio.
    try {
      const viewports = await Promise.all(
        Array.from({ length: np }, async (_, pageIndex) => {
          const page = await getPage(pageIndex + 1);
          return page.getViewport({ scale: 1 });
        }),
      );
      const nextDimensions = new Map<number, PdfPageDimensions>();
      for (const [pageIndex, viewport] of viewports.entries()) {
        if (viewport.width > 0 && viewport.height > 0) {
          nextDimensions.set(pageIndex, { width: viewport.width, height: viewport.height });
        }
      }
      setPageDimensions(nextDimensions);

      const firstPage = nextDimensions.get(0);
      if (firstPage) {
        setPageAspectRatio(firstPage.height / firstPage.width);
      }
    } catch {
      // Keep fallback aspect ratio when PDF page metadata is unavailable.
    }

    setDocLoadCount((c) => c + 1);
  }, []);

  // Keep track of available viewport width for fit-to-screen rendering.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let measuredWidth: number | undefined;
    const updateWidth = () => {
      const styles = window.getComputedStyle(container);
      const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
      const nextWidth = Math.max(1, container.clientWidth - paddingLeft - paddingRight);
      if (nextWidth === measuredWidth) return;

      measuredWidth = nextWidth;
      setContainerWidth(nextWidth);
    };

    updateWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  // Clear cached page heights synchronously whenever scale changes, so the
  // very same render that picks up the new scale also drops heights measured
  // at the old one. Doing this in a useEffect keyed on `scale` instead would
  // run AFTER the scale-change render has already committed (and mutating a
  // ref doesn't itself trigger a re-render), so off-screen placeholders would
  // reuse stale, old-scale heights for at least one frame — indefinitely if no
  // further re-render happens to occur.
  const setScale = useCallback((update: React.SetStateAction<number>) => {
    measuredHeights.current.clear();
    setScaleState(update);
  }, []);

  // Mirror scale in a ref so the pinch handlers (bound once per doc load) read
  // the latest value without re-attaching listeners on every zoom change.
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  // Scroll restoration after a pinch/wheel zoom commits. Captured BEFORE the
  // scale changes: anchoring to a fraction of a specific page element survives
  // layout shifts that don't scale with the PDF (container padding, fixed page
  // margins) and post-resize scrollTop clamping. The offset fallback (no page
  // elements yet) also snapshots scroll offsets pre-resize for the same reason.
  type PinchAnchor =
    | { kind: 'page'; el: HTMLElement; fractionX: number; fractionY: number; viewportX: number; viewportY: number }
    | { kind: 'offsets'; scrollLeft: number; scrollTop: number; ratio: number; x: number; y: number };
  const pendingPinchAnchor = useRef<PinchAnchor | null>(null);
  useLayoutEffect(() => {
    const anchor = pendingPinchAnchor.current;
    const container = scrollContainerRef.current;
    if (!anchor || !container) return;
    pendingPinchAnchor.current = null;

    if (anchor.kind === 'offsets') {
      container.scrollLeft = (anchor.scrollLeft + anchor.x) * anchor.ratio - anchor.x;
      container.scrollTop = (anchor.scrollTop + anchor.y) * anchor.ratio - anchor.y;
      return;
    }

    // Incremental correction: scroll by however far the anchored point has
    // drifted from the pinch midpoint. Idempotent, so it can safely re-run as
    // page canvases finish resizing at the new scale.
    const apply = () => {
      const rect = anchor.el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      container.scrollLeft += rect.left + anchor.fractionX * rect.width - (containerRect.left + anchor.viewportX);
      container.scrollTop += rect.top + anchor.fractionY * rect.height - (containerRect.top + anchor.viewportY);
    };

    apply();
    if (typeof ResizeObserver === 'undefined') return;

    // Rendered pages resize asynchronously when their canvases repaint at the
    // new scale; chase those layout shifts briefly, then hand scroll control
    // back to the user.
    const observer = new ResizeObserver(apply);
    for (const [, ref] of pageRefMap.current) {
      if (ref.current) observer.observe(ref.current);
    }
    const settleTimer = setTimeout(() => observer.disconnect(), 500);
    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, [scale]);

  // Pinch-to-zoom: two-finger touch gestures and trackpad pinch / Ctrl+wheel
  // drive the same `scale` state as the toolbar buttons. During the gesture the
  // <Document> wrapper gets a cheap CSS transform for live feedback; on release
  // the scale commits and the PDF re-renders crisply at the new size. Native
  // browser page-zoom is suppressed inside the viewer (touch-action +
  // preventDefault + iOS gesture events) so the gesture zooms the document
  // instead of the whole UI.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const previousTouchAction = container.style.touchAction;
    container.style.touchAction = 'pan-x pan-y';

    let pinching = false;
    let wheeling = false;
    let wheelTimer: ReturnType<typeof setTimeout> | undefined;
    let startDistance = 0;
    let startScale = 1;
    let currentRatio = 1;
    // Gesture midpoint relative to the container's viewport (for scroll anchoring)
    let mid = { x: 0, y: 0 };
    // Gesture midpoint relative to the untransformed content (for transform-origin)
    let origin = { x: 0, y: 0 };

    const content = () => container.firstElementChild as HTMLElement | null;

    const touchDistance = (touches: TouchList) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

    const resetTransform = () => {
      const el = content();
      if (el) {
        el.style.transform = '';
        el.style.transformOrigin = '';
      }
    };

    const beginGestureAt = (clientX: number, clientY: number) => {
      currentRatio = 1;
      startScale = scaleRef.current;
      const containerRect = container.getBoundingClientRect();
      mid = { x: clientX - containerRect.left, y: clientY - containerRect.top };
      const el = content();
      if (el) {
        const rect = el.getBoundingClientRect();
        origin = { x: clientX - rect.left, y: clientY - rect.top };
      }
    };

    const applyLiveRatio = (rawScale: number) => {
      const clampedScale = Math.min(PDF_MAX_SCALE, Math.max(PDF_MIN_SCALE, rawScale));
      currentRatio = clampedScale / startScale;
      const el = content();
      if (el) {
        el.style.transformOrigin = `${origin.x}px ${origin.y}px`;
        el.style.transform = `scale(${currentRatio})`;
      }
    };

    const commitScale = () => {
      resetTransform();
      if (Math.abs(currentRatio - 1) < 0.01) return;
      const nextScale = +(startScale * currentRatio).toFixed(2);

      // Anchor to the page under the gesture midpoint, measured from the
      // still-untransformed pre-zoom layout (resetTransform just ran).
      const containerRect = container.getBoundingClientRect();
      const midClientX = containerRect.left + mid.x;
      const midClientY = containerRect.top + mid.y;
      let best: { el: HTMLElement; rect: DOMRect; distance: number } | null = null;
      for (const [, ref] of pageRefMap.current) {
        const el = ref.current;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        const distance =
          midClientY < rect.top ? rect.top - midClientY : midClientY > rect.bottom ? midClientY - rect.bottom : 0;
        if (!best || distance < best.distance) best = { el, rect, distance };
      }

      pendingPinchAnchor.current = best
        ? {
            kind: 'page',
            el: best.el,
            fractionX: (midClientX - best.rect.left) / best.rect.width,
            fractionY: (midClientY - best.rect.top) / best.rect.height,
            viewportX: mid.x,
            viewportY: mid.y,
          }
        : {
            kind: 'offsets',
            scrollLeft: container.scrollLeft,
            scrollTop: container.scrollTop,
            ratio: nextScale / startScale,
            x: mid.x,
            y: mid.y,
          };
      setScale(nextScale);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      pinching = true;
      startDistance = touchDistance(event.touches);
      beginGestureAt(
        (event.touches[0].clientX + event.touches[1].clientX) / 2,
        (event.touches[0].clientY + event.touches[1].clientY) / 2,
      );
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pinching || event.touches.length !== 2) return;
      // Stop native scroll/page-zoom while two fingers are down.
      event.preventDefault();
      applyLiveRatio(startScale * (touchDistance(event.touches) / startDistance));
    };

    const endPinch = () => {
      if (!pinching) return;
      pinching = false;
      commitScale();
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) endPinch();
    };

    const endWheel = () => {
      if (!wheeling) return;
      wheeling = false;
      commitScale();
    };

    // Trackpad pinch arrives as wheel events with ctrlKey set; Ctrl/Cmd+wheel
    // zooms too. Events come in bursts, so feedback is a live transform and the
    // crisp re-render commits shortly after the last event.
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      if (!wheeling) {
        wheeling = true;
        beginGestureAt(event.clientX, event.clientY);
      }
      const deltaY = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      applyLiveRatio(startScale * currentRatio * Math.exp(-deltaY * 0.01));
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(endWheel, 150);
    };

    // iOS Safari (<13 and some embedded webviews) fires proprietary gesture
    // events for pinch; preventing them blocks native page zoom in the viewer.
    const preventGesture = (event: Event) => event.preventDefault();

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('gesturestart', preventGesture as EventListener);
    container.addEventListener('gesturechange', preventGesture as EventListener);

    return () => {
      clearTimeout(wheelTimer);
      // docLoadCount can bump mid-gesture (page dims resolve async); the new
      // effect instance starts un-pinched, so clear our transform or it sticks.
      resetTransform();
      container.style.touchAction = previousTouchAction;
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('gesturestart', preventGesture as EventListener);
      container.removeEventListener('gesturechange', preventGesture as EventListener);
    };
  }, [docLoadCount, setScale]);

  // Track which page is most visible via IntersectionObserver → update currentPage
  // Re-runs when the document reloads (docLoadCount) or scale changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    const visibilityMap = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageIdx = Number((entry.target as HTMLElement).dataset.pageIndex);
          visibilityMap.set(pageIdx, entry.intersectionRatio);
        }
        // Find page with highest visibility
        let bestPage = 0;
        let bestRatio = -1;
        for (const [pageIdx, ratio] of visibilityMap) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = pageIdx;
          }
        }
        // currentPage is 1-indexed
        setCurrentPage(bestPage + 1);
      },
      {
        root: container,
        threshold: [0, 0.25, 0.5, 0.75, 1.0],
      },
    );

    // Observe all page elements
    for (const [, ref] of pageRefMap.current) {
      if (ref.current) observer.observe(ref.current);
    }

    return () => observer.disconnect();
     
  }, [numPages, docLoadCount, scale, pdfUrl, setCurrentPage]);

  // Lazy-rendering observer: track which pages are near the viewport so we
  // only mount the heavy <Page> + field overlays for nearby pages.
  // Uses a generous rootMargin so pages render slightly before scrolling into view.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setNearViewport((prev) => {
          const next = new Set(prev);
          for (const entry of entries) {
            const pageIdx = Number((entry.target as HTMLElement).dataset.pageIndex);
            if (entry.isIntersecting) {
              next.add(pageIdx);
            } else {
              next.delete(pageIdx);
            }
          }
          // Only trigger re-render if the set actually changed
          if (next.size === prev.size && [...next].every((v) => prev.has(v))) return prev;
          return next;
        });
      },
      {
        root: container,
        // Render pages within 1500px above/below the visible area
        rootMargin: '1500px 0px',
      },
    );

    for (const [, ref] of pageRefMap.current) {
      if (ref.current) observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [numPages, docLoadCount, scale, pdfUrl]);

  // Mobile: fit PDF width to the viewer; manual zoom remains a multiplier.
  // Desktop: keep the original scale-based rendering.
  const getEffectiveScale = useCallback(
    (pageIndex: number) => {
      if (isDesktop) return scale;
      return mobileRenderWidth / (pageDimensions.get(pageIndex)?.width ?? 612);
    },
    [isDesktop, mobileRenderWidth, pageDimensions, scale],
  );

  const pageIndices = useMemo(() => Array.from({ length: numPages }, (_, i) => i), [numPages]);

  return {
    isDesktop,
    numPages,
    scale,
    setScale,
    pageAspectRatio,
    currentPage,
    scrollContainerRef,
    getPageRef,
    nearViewport,
    measuredHeights,
    onDocumentLoadSuccess,
    fitWidth,
    mobileRenderWidth,
    getEffectiveScale,
    pageIndices,
  };
}
